// Persistencia del estado de la sub-vista Casos en localStorage — lógica PURA y testeada
// (persistencia.test.ts). Guarda ids, no tarjetas: las URLs firmadas vencen a la hora, así
// que al volver se re-piden los casos por id (POST /casos) con URLs frescas.
//
// TODO acceso al storage va en try/catch y se ignora si falla (cuota, modo privado,
// iframe sin storage): perder el guardado es molesto; romper la pestaña, no.

import type { ConfigFormulario, ParamsDataset, Universo } from './tipos';

export const CLAVE_CASOS = 'test-masivo.analisis.casos';
const VERSION = 1 as const;

/** Lo mínimo que se usa de localStorage, inyectable para testear sin DOM. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface EstadoCasos {
  params: ParamsDataset;
  universo: Universo | null;
  imagen_ids: string[];
  control_ids: string[];
  sel_editadas: string[];
  sel_control: string[];
  formulario: ConfigFormulario | null;
  /** Etiqueta de la corrida de la que salieron los casos (plantilla), o null si vino del dataset. */
  origen: string | null;
}

export interface EstadoCasosGuardado extends EstadoCasos {
  version: typeof VERSION;
  savedAt: string;
}

export function serializarEstadoCasos(estado: EstadoCasos, ahora: Date = new Date()): string {
  const guardado: EstadoCasosGuardado = { version: VERSION, savedAt: ahora.toISOString(), ...estado };
  return JSON.stringify(guardado);
}

const esObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const esStr = (x: unknown): x is string => typeof x === 'string';
const esNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const listaStr = (x: unknown): string[] | null => (Array.isArray(x) && x.every(esStr) ? (x as string[]) : null);

function parsearParams(x: unknown): ParamsDataset | null {
  if (!esObj(x)) return null;
  const { rubro, desde, prioridad_max, por_empresa, limit, control } = x;
  if (!esStr(rubro) || !esStr(desde) || !esNum(por_empresa) || !esNum(limit) || !esNum(control)) return null;
  if (prioridad_max !== 1 && prioridad_max !== 2 && prioridad_max !== 3) return null;
  return { rubro, desde, prioridad_max, por_empresa, limit, control };
}

function parsearUniverso(x: unknown): Universo | null {
  if (!esObj(x)) return null;
  const { total, empresas, prio1, prio2, prio3 } = x;
  if (![total, empresas, prio1, prio2, prio3].every(esNum)) return null;
  return { total: total as number, empresas: empresas as number, prio1: prio1 as number, prio2: prio2 as number, prio3: prio3 as number };
}

function parsearFormulario(x: unknown): ConfigFormulario | null {
  if (!esObj(x)) return null;
  const { etiqueta, modelo, effort, fotos, prompt } = x;
  if (!esStr(etiqueta) || !esStr(modelo) || !esStr(effort) || !esStr(prompt)) return null;
  if (fotos !== 'opt' && fotos !== 'full') return null;
  return { etiqueta, modelo, effort, fotos, prompt };
}

/**
 * Valida campo por campo: un JSON viejo o manipulado da null (→ defaults) en vez de
 * un estado a medias. Las selecciones se recortan a los ids que existen.
 */
export function parsearEstadoCasos(raw: string | null): EstadoCasosGuardado | null {
  if (!raw) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!esObj(json) || json.version !== VERSION || !esStr(json.savedAt)) return null;
  const params = parsearParams(json.params);
  const imagen_ids = listaStr(json.imagen_ids);
  const control_ids = listaStr(json.control_ids);
  const sel_editadas = listaStr(json.sel_editadas);
  const sel_control = listaStr(json.sel_control);
  if (!params || !imagen_ids || !control_ids || !sel_editadas || !sel_control) return null;
  const setE = new Set(imagen_ids);
  const setC = new Set(control_ids);
  return {
    version: VERSION,
    savedAt: json.savedAt,
    params,
    universo: parsearUniverso(json.universo),
    imagen_ids,
    control_ids,
    sel_editadas: sel_editadas.filter((id) => setE.has(id)),
    sel_control: sel_control.filter((id) => setC.has(id)),
    formulario: parsearFormulario(json.formulario),
    origen: esStr(json.origen) ? json.origen : null,
  };
}

export function guardarEstadoCasos(storage: StorageLike | null | undefined, estado: EstadoCasos): void {
  if (!storage) return;
  try {
    storage.setItem(CLAVE_CASOS, serializarEstadoCasos(estado));
  } catch (e) {
    console.warn('[analisis] no se pudo guardar el estado de Casos:', e);
  }
}

export function leerEstadoCasos(storage: StorageLike | null | undefined): EstadoCasosGuardado | null {
  if (!storage) return null;
  try {
    return parsearEstadoCasos(storage.getItem(CLAVE_CASOS));
  } catch (e) {
    console.warn('[analisis] no se pudo leer el estado de Casos:', e);
    return null;
  }
}

export function borrarEstadoCasos(storage: StorageLike | null | undefined): void {
  if (!storage) return;
  try {
    storage.removeItem(CLAVE_CASOS);
  } catch (e) {
    console.warn('[analisis] no se pudo borrar el estado de Casos:', e);
  }
}

/** El localStorage real, o null si el navegador no lo expone (acceder puede tirar). */
export function storageDelNavegador(): StorageLike | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}
